import { Layout, Menu, Avatar, Dropdown, theme, Button, App } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  GlobalOutlined,
  MoonOutlined,
  SunOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { buildMainMenuItems } from '@/constants/navigation.constants';
import { useNavigationGuard } from '@/contexts/NavigationGuardContext';
import { useUnsavedChangesModal } from '@/components/common/ConfirmModal';
import { authApi } from '@/services/api/auth.api';
import { useFeatureFlags } from '@/features/flags';

const { Header, Content } = Layout;

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, toggleTheme } = useTheme();
  const { token } = theme.useToken();
  const { i18n, t } = useTranslation('common');
  const { hasUnsavedChanges } = useNavigationGuard();
  const { confirmUnsavedChanges } = useUnsavedChangesModal();
  const { isEnabled, flags } = useFeatureFlags();

  // Confirmation function for navigation when there are unsaved changes
  const confirmBeforeNavigate = (callback: () => void) => {
    if (hasUnsavedChanges) {
      confirmUnsavedChanges(callback);
    } else {
      callback();
    }
  };

  // Language menu items
  const languageMenuItems: MenuProps['items'] = [
    {
      key: 'en',
      label: t('languages.english'),
      icon: i18n.language === 'en' ? <CheckOutlined /> : null,
      onClick: () => i18n.changeLanguage('en'),
    },
    {
      key: 'zh',
      label: t('languages.chinese'),
      icon: i18n.language === 'zh' ? <CheckOutlined /> : null,
      onClick: () => i18n.changeLanguage('zh'),
    },
  ];

  // Get current language label
  const getCurrentLanguageLabel = () => {
    return i18n.language === 'zh' ? '中文' : 'EN';
  };

  // Top horizontal menu — filtered by feature flags (recompute when flags change)
  const menuItems: MenuProps['items'] = buildMainMenuItems({
    navigate,
    t,
    confirmBeforeNavigate,
    isEnabled,
  });
  // Reference flags so React re-renders menu when toggles change
  void flags;

  // User dropdown menu
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('menu.profile'),
      onClick: () => navigate('/auth/user-profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('menu.settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('menu.logout'),
      danger: true,
      onClick: async () => {
        try {
          await authApi.logout();
        } catch {
          // Ignore logout API errors
        }
        // Redirect to login page
        window.location.href = '/login';
      },
    },
  ];

  // Get selected key from current path
  const getSelectedKey = () => {
    const path = location.pathname;
    const prefixes = [
      '/auth/vendors',
      '/auth/governance',
      '/auth/customers',
      '/auth/categories',
      '/auth/products',
      '/auth/product-taxonomy',
      '/auth/contacts',
      '/auth/sales-orders',
      '/auth/claims',
      '/auth/purchase-orders',
      '/auth/occ',
      '/auth/shipments',
      '/auth/shipping-notices',
      '/auth/goods-receipts',
      '/auth/finance/ap',
      '/auth/finance/ar',
      '/auth/finance/gl',
      // S1.4 — the 系統 nav item is keyed /auth/pricing/setup (its real
      // destination); the selected-key prefix must match the live URL family
      // (/auth/pricing/setup/:tab) or the highlight falls to master-data.
      '/auth/pricing/setup',
      '/auth/engines/costing',
      '/auth/reports/order-margin',
      '/auth/reports/back-order',
      '/auth/reports/unreceived',
      '/auth/reports/receivables',
      '/auth/reports/payables',
    ];
    const hit = prefixes.find((p) => path === p || path.startsWith(`${p}/`));
    return hit || 'master-data';
  };

  return (
    <App>
      <style>
        {`
          /* Hide scrollbar for Chrome, Safari and Opera */
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }

          /* Hide scrollbar for IE, Edge and Firefox */
          .hide-scrollbar {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;  /* Firefox */
          }
        `}
      </style>
      <Layout className="tw-h-screen tw-flex tw-flex-col">
        <Header
          className="tw-flex tw-items-center tw-justify-between tw-flex-shrink-0"
          style={{
            padding: `0 ${token.paddingLG}px`,
          }}
        >
          <div className="tw-flex tw-items-center tw-flex-1" style={{ gap: token.marginLG }}>
            {/* Logo — purple mark + SenditSell wordmark */}
            <div
              className="tw-flex-shrink-0 tw-flex tw-items-center"
              style={{ gap: token.marginSM, cursor: 'pointer' }}
              onClick={() => confirmBeforeNavigate(() => navigate('/auth/vendors'))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  confirmBeforeNavigate(() => navigate('/auth/vendors'));
                }
              }}
            >
              <img
                src="/logo.svg"
                alt=""
                width={28}
                height={27}
                style={{ display: 'block', flexShrink: 0 }}
              />
              <span
                style={{
                  fontSize: token.fontSizeHeading4,
                  fontWeight: token.fontWeightStrong,
                  color: token.colorPrimary,
                  lineHeight: 1,
                }}
              >
                {t('app.title')}
              </span>
            </div>

            {/* Horizontal Navigation */}
            <Menu
              mode="horizontal"
              selectedKeys={[getSelectedKey()]}
              items={menuItems}
              className="tw-border-none tw-flex-1"
              overflowedIndicator={null}
              style={{ minWidth: 0 }}
            />
          </div>

          {/* Right side: Language Switcher + Theme Toggle + User Profile */}
          <div className="tw-flex tw-items-center tw-gap-4">
            {/* Language Switcher */}
            <Dropdown
              menu={{ items: languageMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="text"
                icon={<GlobalOutlined />}
                style={{
                  height: token.controlHeight,
                  color: token.colorPrimary,
                }}
                styles={{
                  icon: {
                    color: token.colorPrimary,
                    fontSize: token.sizeSM,
                  },
                }}
              >
                <span>{getCurrentLanguageLabel()}</span>
              </Button>
            </Dropdown>

            {/* Theme Toggle */}
            <Button
              type="text"
              icon={mode === 'light' ? <MoonOutlined /> : <SunOutlined />}
              onClick={toggleTheme}
              style={{
                height: token.controlHeight,
                color: token.colorPrimary,
              }}
              styles={{
                icon: {
                  color: token.colorPrimary,
                  fontSize: token.sizeSM,
                },
              }}
            />

            {/* User Profile */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div
                className="tw-flex tw-items-center"
                style={{ cursor: 'pointer', height: token.controlHeight }}
              >
                <Avatar icon={<UserOutlined />} style={{ backgroundColor: token.colorPrimary }} />
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="tw-flex-1 tw-overflow-y-auto hide-scrollbar">
          <Outlet />
        </Content>
      </Layout>
    </App>
  );
};

export default MainLayout;
